import React, { useEffect, useState, useRef } from 'react';
import { Folder, FileText, Download, Upload, Trash2, FolderPlus, GripVertical, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApi, useUserContext } from './DashboardLayout';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; }
}

function fileColor(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  const colors = { pdf: '#EF4444', doc: '#3B82F6', docx: '#3B82F6', xls: '#10B981', xlsx: '#10B981', zip: '#8B5CF6' };
  return colors[ext] || '#6B7280';
}

export default function ProviderDocumentsPage({ providerId, providerName, embedded = false }) {
  const { apiFetch } = useApi();
  const { user, userType } = useUserContext();
  const isAdmin = userType <= 2;
  const activeProviderId = providerId || user?.id;

  const [folderInfo, setFolderInfo] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [showNewSub, setShowNewSub] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { if (activeProviderId) loadFolder(); }, [activeProviderId]);

  const loadFolder = async () => {
    setLoading(true);
    const r = await apiFetch(`/api/downloads/provider-folder/${activeProviderId}`);
    const d = await r?.json();
    if (d && !d.detail) setFolderInfo(d);
    setLoading(false);
  };

  const loadFiles = async (subfolder) => {
    setFilesLoading(true);
    const r = await apiFetch(`/api/downloads/provider-folder/${activeProviderId}/files?subfolder=${encodeURIComponent(subfolder)}`);
    const d = await r?.json();
    if (Array.isArray(d)) setFiles(d);
    setFilesLoading(false);
  };

  const selectSub = (sub) => {
    setSelectedSub(sub);
    loadFiles(sub.name);
  };

  const downloadFile = async (file) => {
    const r = await apiFetch(`/api/downloads/file/link?path=${encodeURIComponent(file.path)}`);
    const d = await r?.json();
    if (d?.url) window.open(d.url, '_blank');
  };

  const deleteFile = async (file) => {
    if (!window.confirm(`"${file.name}" wirklich löschen?`)) return;
    await apiFetch('/api/downloads/file', { method: 'DELETE', body: JSON.stringify({ path: file.path }) });
    loadFiles(selectedSub.name);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSub) return;

    if (!isAdmin && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Nur PDF-Dateien sind erlaubt.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const subfolder = selectedSub.name;
    await apiFetch(`/api/downloads/provider-folder/${activeProviderId}/upload?subfolder=${encodeURIComponent(subfolder)}`, {
      method: 'POST', body: formData
    });
    setUploading(false);
    loadFiles(subfolder);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const createSubfolder = async () => {
    if (!newSubName.trim()) return;
    await apiFetch(`/api/downloads/provider-folder/${activeProviderId}/create-subfolder`, {
      method: 'POST', body: JSON.stringify({ name: newSubName.trim() })
    });
    setNewSubName(''); setShowNewSub(false);
    loadFolder();
  };

  // Provider can upload only in "Uploads" folder
  const canUpload = isAdmin || (selectedSub?.name === 'Uploads');

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  if (!folderInfo) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
      <p>Kein Dokumentenordner vorhanden.</p>
      {isAdmin && <button className="btn btn-primary btn-sm" onClick={loadFolder}>Ordner erstellen</button>}
    </div>
  );

  return (
    <div data-testid="provider-documents-page">
      {!embedded && (
        <div className="page-header">
          {isAdmin && (
            <Link to="/app/provider-documents-overview" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 10, textDecoration: 'none' }} data-testid="back-to-overview">
              <ArrowLeft size={15} /> Zurück zur Übersicht
            </Link>
          )}
          {userType === 4 && providerId && (
            <Link to="/app/my-documents" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 10, textDecoration: 'none' }} data-testid="back-to-my-documents">
              <ArrowLeft size={15} /> Zurück zur Übersicht
            </Link>
          )}
          <h2>{providerName ? `Dokumente: ${providerName}` : 'Unsere Dokumente'}</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {isAdmin ? `Ordner: ${folderInfo.folder_name}` : 'Verträge, Rechnungen und weitere Unterlagen'}
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 12 }}>
        {/* Subfolders */}
        <div className="of-card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="of-section" style={{ marginBottom: 0 }}>Ordner</div>
            {isAdmin && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowNewSub(true)} style={{ fontSize: 11 }}
                data-testid="new-subfolder-btn"><FolderPlus size={12} /> Neu</button>
            )}
          </div>

          {showNewSub && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6 }}>
              <input className="of-input" value={newSubName} onChange={e => setNewSubName(e.target.value)}
                placeholder="Ordnername" autoFocus onKeyDown={e => e.key === 'Enter' && createSubfolder()}
                style={{ fontSize: 12, padding: '4px 8px' }} data-testid="new-subfolder-input" />
              <button className="btn btn-primary btn-sm" onClick={createSubfolder}
                disabled={!newSubName.trim()} style={{ fontSize: 11, padding: '4px 10px' }}>OK</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewSub(false); setNewSubName(''); }}
                style={{ fontSize: 11, padding: '4px 8px' }}>X</button>
            </div>
          )}

          {folderInfo.subfolders?.map(sub => (
            <div key={sub.path} data-testid={`subfolder-${sub.name}`}
              onClick={() => selectSub(sub)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', cursor: 'pointer',
                borderBottom: '1px solid var(--border)', transition: 'background 0.15s',
                background: selectedSub?.path === sub.path ? 'var(--primary)' : 'transparent',
                color: selectedSub?.path === sub.path ? '#fff' : 'var(--text-primary)',
              }}>
              <Folder size={16} style={{ opacity: 0.7, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{sub.name}</span>
            </div>
          ))}

          {(!folderInfo.subfolders || folderInfo.subfolders.length === 0) && (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Keine Unterordner
            </div>
          )}
        </div>

        {/* Files */}
        <div className="of-card" style={{ padding: 0 }}>
          {!selectedSub ? (
            <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Folder size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>Wählen Sie einen Ordner aus</p>
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="of-section" style={{ marginBottom: 0 }}>{selectedSub.name}</div>
                {canUpload && (
                  <div>
                    <input type="file" ref={fileInputRef} onChange={uploadFile}
                      accept={!isAdmin ? ".pdf" : undefined}
                      style={{ display: 'none' }} data-testid="provider-file-upload-input" />
                    <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      style={{ fontSize: 12 }} data-testid="provider-upload-btn">
                      <Upload size={14} /> {uploading ? 'Hochladen...' : isAdmin ? 'Datei hochladen' : 'PDF hochladen'}
                    </button>
                  </div>
                )}
              </div>

              {!isAdmin && selectedSub.name === 'Uploads' && (
                <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', background: '#FEF3C7', fontSize: 12, color: '#92400E' }}>
                  Hier können Sie PDF-Dokumente für uns hochladen. Diese werden von uns geprüft.
                </div>
              )}

              {filesLoading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>
              ) : files.length === 0 ? (
                <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <FileText size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>Keine Dateien vorhanden</p>
                </div>
              ) : (
                files.map(f => (
                  <div key={f.path} data-testid={`prov-file-${f.name}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: fileColor(f.name) + '15', flexShrink: 0 }}>
                      <FileText size={18} style={{ color: fileColor(f.name) }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 12 }}>
                        <span>{formatSize(f.size)}</span>
                        <span>{formatDate(f.modified)}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button className="btn-icon" onClick={() => downloadFile(f)} title="Herunterladen"
                        style={{ color: 'var(--primary)' }}><Download size={16} /></button>
                      {isAdmin && (
                        <button className="btn-icon" onClick={() => deleteFile(f)} title="Löschen"
                          style={{ color: '#EF4444' }}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
